import { FluidSize } from '@lmig/fluid-core';
import { FunctionalComponent, h } from '@stencil/core';

import { extractIconPopoverConfig } from '../../../utils/mapping/mapping.utils';
import { themeVar } from '../../../utils/themes/theme.variables';
import { stripFluidData } from '../internal/fluid-table-editable-cell-management.utils';
import { actionDeprecation } from '../internal/fluid-table-misc.utils';

/**
 * Renders the loading spinner when data is loading
 * @param props
 * @constructor
 */
export const Loader = (props) => {
  return props.dataLoading ? (
    <div class={'loading-spinner'}>
      <fluid-loading-spinner />
    </div>
  ) : undefined;
};

/**
 * Horizontal Overflow Left/Right Shadow
 */
interface OverflowShadowProps {
  position: 'left' | 'right';
  shadowVisible: boolean;
  tableCanScroll: boolean;
  tableOverflowsX: boolean;
}

export const OverflowShadow: FunctionalComponent<OverflowShadowProps> = (
  props: OverflowShadowProps
) => {
  const { position, shadowVisible, tableCanScroll, tableOverflowsX } = props;
  return (
    <div
      role='presentation'
      class={{
        [`overflow-shadow-${position}`]: true,
        'shadow-inert': !tableCanScroll || !tableOverflowsX,
        'shadow-hidden': !shadowVisible,
        'shadow-visible': shadowVisible,
      }}
    />
  );
};

// ================================================================ //
// -- TableActionButton (rowActions: combined actions)

export const TableActionButton = (props) => {
  const {
    action,
    dataItem,
    actionClicked,
    retainTransientProperties,
    disabled,
    theme,
    handleCellClicked,
    identifier,
  } = props;
  actionDeprecation(action);

  const iconConfig = !!action.icon && { ...action.icon }; // Clone iconConfig since extractIconPopoverConfig() mutates it
  const dynamicIconConfig =
    !!action.dynamicIcon && action.dynamicIcon(dataItem);
  const isDisabled =
    disabled || iconConfig?.disabled || dynamicIconConfig?.disabled;
  const popoverConfig = extractIconPopoverConfig(
    dynamicIconConfig || iconConfig,
    { tagName: 'FLUID-TABLE' } as HTMLFluidTableElement
  );

  const handleActionClick = () => {
    if (isDisabled) return;
    handleCellClicked();
    actionClicked.emit({
      actionKey: action.actionKey,
      rowData: stripFluidData({ ...dataItem }, retainTransientProperties),
    });
  };

  if (!iconConfig && !dynamicIconConfig && !action.useIcon) return;

  return (
    <fluid-button
      data-test-id={`${identifier}-action-btn`}
      identifier={identifier}
      id={action.identifier}
      onClick={() => handleActionClick()}
      config={{
        disabled: isDisabled, // Button is disabled (instead of embedded Icon) so that Icon color is correct
        popoverConfig, // Implement Icon's Popover on parent Button (better usability)
        small: true,
        icon: {
          key: action.useIcon,
          size: themeVar<FluidSize>(theme, 'icons.default_size'),
          ...iconConfig, // Merge here to override "key" & "size" defaults (if necessary)
          ...(!!dynamicIconConfig && dynamicIconConfig),
          disabled: false, // False to prevent "double disabled states" (i.e. Icon AND Button)
        },
      }}
    />
  );
};
